import { useMemo } from 'react';
import { Grid } from '@mui/material';

import { Divider, CardContent } from '@occc/ui-design-system';
import {
  RetailerNetworkPerformanceFragment,
  Retailer_Network_Performance_Period,
} from '@occc/graphql';

import { parseRetailerNetworkPerformanceChartData } from './components/retailer-network-performance-chart/retailer-network-performance-chart.utils';
import {
  formatPerformanceNumber,
  parseRetailerNetworkPerformanceDetails,
  formatPerformanceAmount,
} from './retailer-network-performance.utils';
import { RetailerNetworkPerformanceScope } from './retailer-network-performance.types';
import { RetailerNetworkPerformanceChart } from './components/retailer-network-performance-chart/retailer-network-performance-chart.component';

import { RetailerNetworkPerformanceInfoItem } from '../retailer-network-performance-info-item.component';

type RetailerNetworkPerformanceProps = {
  retailerNetworkPerformance?: RetailerNetworkPerformanceFragment;
  period: Retailer_Network_Performance_Period;
  isSpecificGoalSet?: boolean;
  openSpecificGoalDialog: () => void;
  scope: RetailerNetworkPerformanceScope;
  isUserPermittedToEditSpecificGoals: boolean;
};

export const RetailerNetworkPerformance = ({
  retailerNetworkPerformance,
  period,
  isSpecificGoalSet,
  openSpecificGoalDialog,
  scope,
  isUserPermittedToEditSpecificGoals,
}: RetailerNetworkPerformanceProps) => {
  const performanceDetails = useMemo(
    () => parseRetailerNetworkPerformanceDetails(retailerNetworkPerformance),
    [retailerNetworkPerformance],
  );
  const performanceChartData = useMemo(
    () => parseRetailerNetworkPerformanceChartData(retailerNetworkPerformance),
    [retailerNetworkPerformance],
  );

  return (
    <CardContent>
      {performanceChartData && (
        <>
          <Grid container justifyContent='center' spacing={3}>
            <Grid item xs={12} md={6}>
              <RetailerNetworkPerformanceChart
                chartTitle='New Members'
                chartName='newMembers'
                chartData={performanceChartData.newMembersChartData}
                progress={performanceChartData.newMembersGoalProgress.current}
                targetGoal={performanceChartData.newMembersGoalProgress.target}
                period={period}
                isSpecificGoalSet={isSpecificGoalSet}
                openSpecificGoalDialog={openSpecificGoalDialog}
                scope={scope}
                isUserPermittedToEditSpecificGoals={isUserPermittedToEditSpecificGoals}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <RetailerNetworkPerformanceChart
                chartTitle='Loyalty Transactions'
                chartName='loyaltyTransactions'
                chartData={performanceChartData.loyaltyTransactionsChartData}
                progress={performanceChartData.loyaltyTransactionsGoalProgress.current}
                targetGoal={performanceChartData.loyaltyTransactionsGoalProgress.target}
                period={period}
                isSpecificGoalSet={isSpecificGoalSet}
                openSpecificGoalDialog={openSpecificGoalDialog}
                scope={scope}
                isUserPermittedToEditSpecificGoals={isUserPermittedToEditSpecificGoals}
              />
            </Grid>
          </Grid>

          <Divider sx={{ mb: 3 }} />
        </>
      )}
      <Grid container spacing={3}>
        <Grid item xs={4} md={2.4}>
          <RetailerNetworkPerformanceInfoItem
            performanceTitle='New Members'
            performanceValueTooltipTitle={performanceDetails.newMembersProgress}
            performanceValue={formatPerformanceNumber(performanceDetails.newMembersProgress)}
            avgTitle='Per Store Avg'
            avgValue={formatPerformanceNumber(performanceDetails.newMembersPerStoreAvg)}
          />
        </Grid>
        <Grid item xs={4} md={2.4}>
          <RetailerNetworkPerformanceInfoItem
            performanceTitle='All Trx'
            performanceValueTooltipTitle={performanceDetails.loyaltyTransactions}
            performanceValue={formatPerformanceNumber(performanceDetails.loyaltyTransactions)}
            avgTitle='Per Store Avg'
            avgValue={formatPerformanceNumber(performanceDetails.loyaltyTransactionsPerStoreAvg)}
          />
        </Grid>
        <Grid item xs={4} md={2.4}>
          <RetailerNetworkPerformanceInfoItem
            performanceTitle='Fuel Only Trx'
            performanceValueTooltipTitle={performanceDetails.fuelAmount}
            performanceValue={formatPerformanceAmount(performanceDetails.fuelAmount)}
            avgTitle='Per Transaction Avg'
            avgValue={formatPerformanceAmount(performanceDetails.fuelAmountPerTransactionAvg)}
          />
        </Grid>
        <Grid item xs={4} md={2.4}>
          <RetailerNetworkPerformanceInfoItem
            performanceTitle='Fuel (in gallons)'
            performanceValueTooltipTitle={performanceDetails.fuelVolume}
            performanceValue={formatPerformanceNumber(performanceDetails.fuelVolume)}
            avgTitle='Per Transaction Avg'
            avgValue={formatPerformanceNumber(performanceDetails.fuelVolumePerTransactionAvg)}
          />
        </Grid>
        <Grid item xs={4} md={2.4}>
          <RetailerNetworkPerformanceInfoItem
            performanceTitle='Merch Only Trx'
            performanceValueTooltipTitle={performanceDetails.merchandise}
            performanceValue={formatPerformanceAmount(performanceDetails.merchandise)}
            avgTitle='Per Transaction Avg'
            avgValue={formatPerformanceAmount(performanceDetails.merchandisePerTransactionAvg)}
          />
        </Grid>
      </Grid>
    </CardContent>
  );
};
